import { BookcoverFillIcon, Button, type ButtonProps, ChevronDownIcon, Chip, cn, PlusIcon, PlusSmallIcon, SquareGridFillIcon } from '@zep-us/zus';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { setReferrer } from '@/shared/features/enter-referrer-tracking';
import { useIsInGoogleClassroom } from '@/shared/googleClassroom';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/ui';
import { type EventProperties } from '@/utils/analytics';
import { trackGnbAnalytics } from './trackGnbAnalytics';
type HeaderButtonSize = NonNullable<ButtonProps['size']>;
export function CreateButton({
  type = 'normal',
  compact = false,
  hideRightSlot = false,
  className,
  size
}: {
  type?: 'drawer' | 'normal';
  compact?: boolean;
  hideRightSlot?: boolean;
  className?: string;
  size?: HeaderButtonSize;
}) {
  const {
    t
  } = useTranslation('common');
  const router = useRouter();
  const isInGoogleClassroom = useIsInGoogleClassroom();
  const buttonSize = type === 'drawer' ? 'lg' : size ?? (compact ? 'sm' : 'md');
  const iconSize = type === 'drawer' ? 'md' : size === 'xs' ? 'xs' : size === 'sm' ? 'sm' : compact ? 'xs' : 'sm';
  const trackCreate = (contentType: string, from: 'header_make_button' | 'header_make_dropdown') => {
    const eventProperties: EventProperties = {
      content_type: contentType,
      from
    };
    if (isInGoogleClassroom) {
      eventProperties.extension = 'google_classroom';
    }
    trackGnbAnalytics('create', 'Header', eventProperties);
  };
  return <DropdownMenu modal={true} data-sentry-element="DropdownMenu" data-sentry-component="CreateButton" data-sentry-source-file="CreateButton.tsx">
      <DropdownMenuTrigger asChild data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="CreateButton.tsx">
        <Button onClick={() => {
        logger.track('public_header_create_click');
      }} variant="contained" intent={type === 'drawer' ? 'alternative' : 'strong'} size={buttonSize} leftSlot={<PlusIcon size={iconSize} fill={type === 'drawer' ? ICON_COLOR.strong : ICON_COLOR.inverse} />} rightSlot={hideRightSlot ? undefined : <ChevronDownIcon size={iconSize} fill={type === 'drawer' ? ICON_COLOR.strong : ICON_COLOR.inverse} className="transition-transform duration-200 group-data-[state=open]:rotate-180" />} className={cn('group', type === 'drawer' ? 'w-full' : '', compact && 'h-[30px] min-h-[30px] rounded-xs px-xs text-caption-lg-semibold', className)} data-sentry-element="Button" data-sentry-source-file="CreateButton.tsx">
          <span className="min-w-0 truncate">{t('gnb.create.button')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={type === 'drawer' ? 'center' : 'end'} className={cn('p-xs rounded-md', type === 'drawer' ? 'z-[10000] w-[var(--radix-dropdown-menu-trigger-width)]' : 'min-w-[300px]')} data-sentry-element="DropdownMenuContent" data-sentry-source-file="CreateButton.tsx">
        <div className="flex flex-col gap-4xs px-sm py-xs">
          <p className="text-body-lg-bold">{t('gnb.create.title')}</p>
          <p className="text-body-sm-medium text-alternative">
            {t('gnb.create.description')}
          </p>
        </div>
        <div className="my-3xs h-px w-full bg-alternative" />
        <DropdownMenuItem className="flex h-auto items-center justify-between gap-sm px-sm py-md" onClick={() => {
        trackCreate('quiz', 'header_make_dropdown');
        setReferrer({
          from: 'header_make_dropdown'
        });
        void router.push('/build');
      }} data-sentry-element="DropdownMenuItem" data-sentry-source-file="CreateButton.tsx">
          <div className="flex items-center gap-sm">
            <BookcoverFillIcon size="sm" className="text-blue-500" data-sentry-element="BookcoverFillIcon" data-sentry-source-file="CreateButton.tsx" />
            <span className="text-body-lg-bold">{t('gnb.create.quiz')}</span>
          </div>
          <PlusSmallIcon size="sm" fill={ICON_COLOR.alternative} data-sentry-element="PlusSmallIcon" data-sentry-source-file="CreateButton.tsx" />
        </DropdownMenuItem>
        <DropdownMenuItem className="flex h-auto items-center justify-between gap-sm px-sm py-md" onClick={() => {
        trackCreate('board', 'header_make_dropdown');
        setReferrer({
          from: 'header_make_dropdown'
        });
        void router.push('/board/new');
      }} data-sentry-element="DropdownMenuItem" data-sentry-source-file="CreateButton.tsx">
          <div className="flex items-center gap-sm">
            <SquareGridFillIcon size="sm" className="text-purple-500" data-sentry-element="SquareGridFillIcon" data-sentry-source-file="CreateButton.tsx" />
            <div className="flex items-center gap-xs">
              <span className="text-body-lg-bold">{t('gnb.create.board')}</span>
              <Chip size="xs" rounded="full" className="border-opacity-assistive bg-yellow-50 px-2xs text-caption-md-semibold text-yellow-900" data-sentry-element="Chip" data-sentry-source-file="CreateButton.tsx">
                Beta
              </Chip>
            </div>
          </div>
          <PlusSmallIcon size="sm" fill={ICON_COLOR.alternative} data-sentry-element="PlusSmallIcon" data-sentry-source-file="CreateButton.tsx" />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>;
}